'use client';

import MagicJump, { JumpTarget } from '@/components/MagicJump';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import Image from '@/components/Image';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

// 类型定义
interface Step {
  title: string;
  description: string;
  type: 'img' | 'video';
  image: string;
  jump_target?: JumpTarget;
}

interface PageData {
  title: string;
  steps: Step[];
}

interface Props {
  pageData: PageData;
  sDPageExp: (
    mkt_name: string,
    pits_index: number,
    pits_jump_type?: string,
    pits_jump_content?: string,
  ) => void;
  deviceType: EPlatform;
}

export default function BecomeAmbassador({ pageData, sDPageExp, deviceType }: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [becomeRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});
  const swiperRef = useRef<SwiperType | null>(null);

  // 曝光埋点
  useEffect(() => {
    if (inView) {
      sDPageExp('如何成为大使', 0);
    }
  }, [inView, sDPageExp]);

  // 视频播放控制
  useEffect(() => {
    if (deviceType !== EPlatform.WEB) {
      // 暂停所有视频
      Object.values(videoRefs.current).forEach((video) => {
        if (video) {
          video.pause();
        }
      });

      // 播放当前视频
      const currentVideo = videoRefs.current[activeStep];
      if (currentVideo && pageData.steps[activeStep]?.type === 'video') {
        currentVideo.play().catch(logger.error);
      }
    }
  }, [activeStep, deviceType, pageData.steps]);

  // 步骤点击处理
  const handleStepClick = (index: number) => {
    setActiveStep(index);
    if (swiperRef.current) {
      // 直接跳转到指定索引，禁用动画
      swiperRef.current.slideTo(index, 0, false);
    }
  };

  // 轮播切换处理
  const handleSlideChange = (swiper: SwiperType) => {
    // 使用realIndex获取真实索引，处理循环情况
    const { realIndex } = swiper;
    setActiveStep(realIndex);
  };

  // 安全检查：确保steps数组存在
  if (!pageData?.steps || !Array.isArray(pageData.steps) || pageData.steps.length === 0) {
    return null;
  }

  return (
    <Box
      ref={becomeRef}
      className="w-full bg-[#EEEEEE] px-[14px] pt-[35px] lg:px-[60px] lg:pt-[50px] 2xl:px-[96px] 2xl:pt-[72px]"
    >
      <style>{`
        video::-webkit-media-controls-enclosure {
          display: none;
        }
        video::-webkit-media-controls {
          display: none;
        }
      `}</style>

      <Box className="mx-auto">
        <h2
          className="font-bb text-[24px] leading-[26px] text-center lg:text-[36px] lg:leading-[44px] 2xl:text-[48px] 2xl:leading-[58px] text-primary-text"
          dangerouslySetInnerHTML={{ __html: pageData.title }}
        />

        {deviceType !== EPlatform.WEB ? (
          // 移动端布局：左侧步骤列表，右侧轮播内容
          <Box className="flex justify-between items-start mt-[30px] pr-[10px]">
            {/* 左侧步骤列表 */}
            <Box className="w-[108px]">
              {pageData.steps.map((step, index) => (
                <Box
                  key={index}
                  className={`relative w-[108px] h-[78px] rounded-[12px] mb-[8px] pt-[26px] pr-[18px] pb-[12px] pl-[12px] box-border transition-colors ${
                    activeStep === index ? 'bg-auxiliary-lilac-purple' : 'bg-primary-white'
                  }`}
                  onClick={() => handleStepClick(index)}
                >
                  {/* 编号 */}
                  <span className="absolute top-[9px] right-[14px] font-bb text-[25px] leading-[30px]">
                    {index + 1}
                  </span>
                  {/* 标题 */}
                  <h4 className="font-bb text-[14px] leading-[17px]">{step.title}</h4>
                  {/* 激活状态指示器 */}
                  {activeStep === index && (
                    <Box className="absolute -right-[7px] top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-[8px] border-l-auxiliary-lilac-purple border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent" />
                  )}
                </Box>
              ))}
            </Box>

            {/* 右侧轮播内容 */}
            <Box className="w-[205px]">
              {/* 描述文字 */}
              <p className="min-h-[81px] pr-[8px] pb-[10px] pl-[11px] box-border font-br text-[14px] text-primary-text leading-[17px] text-left">
                {pageData.steps[activeStep]?.description}
              </p>

              {/* 轮播内容 */}
              <Box className="relative w-full">
                <Swiper
                  className="w-full"
                  slidesPerView={1}
                  spaceBetween={6}
                  loop
                  observer
                  observeParents
                  observeSlideChildren
                  onSlideChange={handleSlideChange}
                  onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                >
                  {pageData.steps.map((step, index) => (
                    <SwiperSlide key={index} className="w-full">
                      <MagicJump item={step.jump_target || { jump_type: 0, Jump_target: '' }}>
                        <Box className="w-full">
                          {(() => {
                            if (step.type === 'img') {
                              return (
                                <Image
                                  src={step.image}
                                  alt={step.title.toLowerCase()}
                                  width={205}
                                  height={150}
                                  className="w-full h-auto block"
                                />
                              );
                            }
                            if (step.type === 'video') {
                              return (
                                <video
                                  ref={(el) => {
                                    videoRefs.current[index] = el;
                                  }}
                                  className="w-full h-auto block"
                                  controls={false}
                                  loop
                                  muted
                                  autoPlay
                                  playsInline
                                  preload="auto"
                                >
                                  <source src={step.image} />
                                </video>
                              );
                            }
                            return null;
                          })()}
                        </Box>
                      </MagicJump>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>
            </Box>
          </Box>
        ) : (
          // 桌面端布局：使用列表布局
          <Box className="mt-[50px] lg:mt-[40px] 2xl:mt-[50px]">
            {/* 第一个循环：文字内容 */}
            <Box className="flex justify-between gap-x-[20px] mb-[32px] lg:gap-x-[16px] lg:mb-[24px] 2xl:gap-x-[20px] 2xl:mb-[32px]">
              {pageData.steps.map((step, index) => (
                <Box key={`text-${index}`} className="w-[370px] lg:w-[300px] 2xl:w-[370px]">
                  {/* 步骤标题 */}
                  <Box className="flex justify-center items-center px-[35px] lg:px-[25px] 2xl:px-[35px]">
                    <span className="font-bb text-[30px] leading-[36px] mr-[8px] lg:text-[40px] lg:leading-[48px] lg:mr-[8px] 2xl:text-[50px] 2xl:leading-[60px] 2xl:mr-[10px]">
                      {index + 1}
                    </span>
                    <h4 className="font-bb text-[16px] leading-[20px] lg:text-[20px] lg:leading-[24px] 2xl:text-[24px] 2xl:leading-[28px]">
                      {step.title}
                    </h4>
                  </Box>

                  {/* 步骤描述 */}
                  <p className="px-[35px] text-center font-br text-[14px] leading-[20px] lg:px-[25px] lg:text-[14px] lg:leading-[20px] 2xl:px-[35px] 2xl:text-[16px] 2xl:leading-[24px]">
                    {step.description}
                  </p>
                </Box>
              ))}
            </Box>

            {/* 第二个循环：图片或视频 */}
            <Box className="flex justify-between gap-x-[20px] lg:gap-x-[16px] 2xl:gap-x-[20px]">
              {pageData.steps.map((step, index) => (
                <Box key={`media-${index}`} className="w-[370px] lg:w-[300px] 2xl:w-[370px]">
                  <MagicJump item={step.jump_target || { jump_type: 0, Jump_target: '' }}>
                    {(() => {
                      if (step.type === 'img') {
                        return (
                          <Image
                            src={step.image}
                            alt={step.title.toLowerCase()}
                            width={370}
                            height={250}
                            className="w-full h-auto block"
                          />
                        );
                      }
                      if (step.type === 'video') {
                        return (
                          <video
                            className="w-full h-auto block"
                            controls={false}
                            autoPlay
                            muted
                            loop
                          >
                            <source src={step.image} type="video/mp4" />
                          </video>
                        );
                      }
                      return null;
                    })()}
                  </MagicJump>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
